import * as React from 'react';
import { useEffect, useState } from 'react';
import {
  Paper, Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  IconButton, Collapse, Chip, Button, Pagination,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { sendServerRequest } from '../../server/request';
import { openGlobalSnack } from '../utils/snack';
import { CircularLoading } from '../utils/loading';

const ENDPOINT = '/server/botErrorLogs';
const PAGE_SIZE = 20;

type BotError = {
  id: string;
  source: string;
  type: string;
  identifier: string | null;
  provider: string | null;
  chat_id: number | null;
  status: number | null;
  message: string;
  response: string | null;
  context: string | null;
  createdAt: string;
};

const SOURCE_LABELS: Record<string, { label: string; color: 'secondary' | 'success' | 'info' | 'default' }> = {
  instagram: { label: 'Instagram', color: 'secondary' },
  whatsapp: { label: 'WhatsApp', color: 'success' },
  evolution: { label: 'Evolution', color: 'info' },
  telegram: { label: 'Telegram', color: 'info' },
  autocontratacao: { label: 'Página auto-contratação', color: 'default' },
  app: { label: 'Aplicativo', color: 'default' },
  bot: { label: 'Bot', color: 'default' },
};

function sourceChip(source: string) {
  return SOURCE_LABELS[source] ?? { label: source, color: 'default' as const };
}

function fmtDate(iso: string) {
  try { return new Date(iso).toLocaleString('pt-BR'); } catch { return iso; }
}

function prettyJson(raw: string | null) {
  if (!raw) return '';
  try { return JSON.stringify(JSON.parse(raw), null, 2); } catch { return raw; }
}

export function BotErrorLogsPanel() {
  const [logs, setLogs] = useState<BotError[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState('');
  const [type, setType] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [chatId, setChatId] = useState('');
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function load(toPage: number) {
    setLoading(true);
    const q = new URLSearchParams({ page: String(toPage), pageSize: String(PAGE_SIZE) });
    if (source) q.set('source', source);
    if (type.trim()) q.set('type', type.trim());
    if (identifier.trim()) q.set('identifier', identifier.trim());
    if (chatId.trim()) q.set('chatId', chatId.trim());
    sendServerRequest('GET', `${ENDPOINT}/list?${q.toString()}`)
      .then((e) => {
        const data = e.data?.data ?? {};
        setLogs(data.logs ?? []);
        setTotal(data.total ?? 0);
        setPage(toPage);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        openGlobalSnack('Não foi possível carregar os logs de erro.', 'error');
      });
  }

  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div style={{ maxWidth: 1000, margin: '0 auto' }}>
      <h3 style={{ marginBottom: 4, textAlign: 'center' }}>Logs de erro do bot</h3>
      <p style={{ fontSize: 12, color: '#000000a0', marginTop: 0, marginBottom: 16, textAlign: 'center' }}>
        Falhas no envio de mensagens, webhooks e integrações (Instagram, WhatsApp, Evolution, Telegram), mais os erros da página de auto-contratação e do aplicativo. Retidos por 30 dias.
      </p>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        <TextField select size="small" label="Canal" value={source} style={{ minWidth: 200 }}
          onChange={(e) => setSource(e.target.value)}>
          <MenuItem value="">Todos</MenuItem>
          {Object.entries(SOURCE_LABELS).map(([k, v]) => (
            <MenuItem key={k} value={k}>{v.label}</MenuItem>
          ))}
        </TextField>
        <TextField size="small" label="Tipo" value={type} style={{ minWidth: 140 }}
          onChange={(e) => setType(e.target.value)} placeholder="send_message" />
        <TextField size="small" label="Identificador" value={identifier} style={{ minWidth: 160 }}
          onChange={(e) => setIdentifier(e.target.value)} />
        <TextField size="small" label="Chat ID" value={chatId} style={{ minWidth: 110 }}
          onChange={(e) => setChatId(e.target.value)} />
        <Button variant="contained" onClick={() => load(1)}>Filtrar</Button>
        <IconButton onClick={() => load(page)}><RefreshIcon /></IconButton>
      </div>

      {loading ? <CircularLoading /> : (
        <Paper variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Data</TableCell>
                <TableCell>Canal</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Identificador</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Mensagem</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" style={{ color: '#00000080' }}>Nenhum log.</TableCell></TableRow>
              )}
              {logs.map((l) => (
                <React.Fragment key={l.id}>
                  <TableRow hover>
                    <TableCell>
                      <IconButton size="small" onClick={() => setExpanded(expanded === l.id ? null : l.id)}>
                        {expanded === l.id ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell style={{ whiteSpace: 'nowrap' }}>{fmtDate(l.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" label={sourceChip(l.source).label}
                        color={sourceChip(l.source).color} variant="outlined" />
                    </TableCell>
                    <TableCell>{l.type ?? '—'}</TableCell>
                    <TableCell style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.identifier ?? '—'}
                    </TableCell>
                    <TableCell>{l.status ?? '—'}</TableCell>
                    <TableCell style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {l.message}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={7} style={{ paddingTop: 0, paddingBottom: 0, border: 0 }}>
                      <Collapse in={expanded === l.id} unmountOnExit>
                        <div style={{ padding: 12, background: '#00000008' }}>
                          <div style={{ fontSize: 12, marginBottom: 8 }}>
                            <b>Conta/origem:</b> {l.provider ?? '—'} &nbsp;|&nbsp;
                            <b>chat_id:</b> {l.chat_id ?? '—'}
                          </div>
                          <div style={{ fontSize: 12, marginBottom: 4 }}><b>Mensagem:</b> {l.message}</div>
                          {l.context && (
                            <>
                              <b style={{ fontSize: 12 }}>Contexto (mensagem que falhou):</b>
                              <pre style={{
                                fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                                maxHeight: 200, overflow: 'auto', margin: '4px 0 8px', background: '#0000000a', padding: 8, borderRadius: 4,
                              }}>{prettyJson(l.context)}</pre>
                            </>
                          )}
                          <b style={{ fontSize: 12 }}>Resposta / erro:</b>
                          <pre style={{
                            fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-all',
                            maxHeight: 260, overflow: 'auto', margin: '4px 0 0', background: '#0000000a', padding: 8, borderRadius: 4,
                          }}>{prettyJson(l.response) || '—'}</pre>
                        </div>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
          <Pagination count={pages} page={page} onChange={(_, p) => load(p)} />
        </div>
      )}
    </div>
  );
}
